import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { useSearchParams } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CircleDot, CreditCard, RefreshCw, X } from "lucide-react";
import {
  Button,
  DefinitionList,
  DetailRow,
  FilterBar,
} from "@/components/ui";
import {
  ClickToCopyId,
  PageFrame,
  QueryLoadError,
  SectionCard,
} from "@/components/common";
import { queryLoadFailure } from "@/lib/queryLoadFailure";
import {
  DataTable,
  DataTablePrimaryText,
  queryLoadState,
  type DataTableColumn,
} from "@/components/table";
import {
  adminPaymentAuditKeys,
  adminPaymentAuditService,
  type AdminExternalPaymentRow,
} from "@/services/adminPaymentAuditService";
import { formatDateTime } from "@/lib/datetime";
import { formatMoney } from "@/lib/money";
import { shortId } from "./formatters";
import { enumLabel, type EnumLabelTranslate } from "@/lib/enumLabel";
import { StatusBadge } from "./shared";
import {
  AdminRegionFilter,
  type AdminRegionFilterValue,
} from "@/components/admin/AdminRegionFilter";
import { RegionCodeBadge } from "@/components/admin/RegionCodeBadge";
import { AdminFilterMenu } from "@/components/admin/AdminFilterMenu";
import {
  pageSkip,
  serverPageWindow,
  useClampUrlPage,
  useUrlPage,
} from "@/hooks/useUrlPage";

/**
 * Rows arrive newest first from the server; columns are not client-sortable because
 * sorting one page would misrepresent the full list.
 */
type ServerOrderColumn = "created";

const PAGE_SIZE = 50;

function providerFlowLabel(
  row: Pick<AdminExternalPaymentRow, "provider" | "flow">,
  t: EnumLabelTranslate,
): string {
  return `${enumLabel("externalList.provider", row.provider, t)} · ${enumLabel("externalList.flow", row.flow, t)}`;
}

export default function AdminPaymentsExternalPaymentsPage() {
  const { t } = useTranslation("payments");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusPaymentId = searchParams.get("paymentId");
  const [page, setPage] = useUrlPage();

  const [regionFilter, setRegionFilter] =
    useState<AdminRegionFilterValue>(null);
  // Status options are counted per region, so a region change clears the status.
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const changeRegion = (region: AdminRegionFilterValue) => {
    setRegionFilter(region);
    setStatusFilter(null);
    setPage(1);
  };
  const changeStatus = (status: string | null) => {
    setStatusFilter(status);
    setPage(1);
  };

  const listParams = {
    skip: pageSkip(page, PAGE_SIZE),
    limit: PAGE_SIZE,
    region_id: regionFilter ?? undefined,
    status: statusFilter ?? undefined,
  };

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    isPlaceholderData,
  } = useQuery({
    queryKey: adminPaymentAuditKeys.externalPaymentList(listParams),
    queryFn: () => adminPaymentAuditService.listExternalPayments(listParams),
    placeholderData: keepPreviousData,
  });

  const statusCountsQuery = useQuery({
    queryKey: adminPaymentAuditKeys.externalPaymentStatusCounts(regionFilter),
    queryFn: () =>
      adminPaymentAuditService.countExternalPaymentsByStatus(regionFilter),
  });
  const statusCounts = statusCountsQuery.data;
  // Without counts the status menu has only "All": say why instead of hiding the options.
  const statusCountsFailure = queryLoadFailure(
    statusCountsQuery,
    t("externalList.statusCountsLoadError"),
  );

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  useClampUrlPage(
    page,
    data && !isPlaceholderData
      ? serverPageWindow(page, PAGE_SIZE, total).totalPages
      : undefined,
  );
  const focusCardRef = useRef<HTMLDivElement>(null);

  const { data: focusedPayment = null } = useQuery({
    queryKey: adminPaymentAuditKeys.externalPayment(focusPaymentId ?? ""),
    queryFn: () =>
      adminPaymentAuditService.getExternalPayment(focusPaymentId!),
    enabled: !!focusPaymentId,
  });

  useEffect(() => {
    if (!focusPaymentId || isLoading || !focusedPayment) return;
    focusCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focusPaymentId, isLoading, focusedPayment]);

  const focusPayment = (paymentId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("paymentId", paymentId);
    setSearchParams(next, { replace: true });
  };

  const clearHighlight = () => {
    if (!focusPaymentId) return;
    const next = new URLSearchParams(searchParams);
    next.delete("paymentId");
    setSearchParams(next, { replace: true });
  };

  const columns = useMemo<
    DataTableColumn<AdminExternalPaymentRow, ServerOrderColumn>[]
  >(
    () => [
      {
        id: "created",
        header: t("shared.colCreated"),
        defaultWidth: 160,
        cell: (row) => (
          <span className="break-words tabular-nums text-surface-700">
            {formatDateTime(row.created_at)}
          </span>
        ),
      },
      {
        id: "amount",
        header: t("shared.colAmount"),
        defaultWidth: 110,
        align: "right",
        cell: (row) => (
          <DataTablePrimaryText className="tabular-nums whitespace-nowrap">
            {formatMoney(row.amount_cents, row.currency)}
          </DataTablePrimaryText>
        ),
      },
      {
        id: "region",
        header: tCommon("region.colCode"),
        defaultWidth: 100,
        cell: (row) => <RegionCodeBadge code={row.region_code} />,
      },
      {
        id: "status",
        header: t("shared.colStatus"),
        defaultWidth: 120,
        cell: (row) => (
          <StatusBadge
            status={row.status}
            label={enumLabel("externalList.paymentStatus", row.status, t)}
          />
        ),
      },
      {
        id: "type",
        header: t("shared.colType"),
        defaultWidth: 190,
        cell: (row) => (
          <div className="min-w-0">
            <DataTablePrimaryText className="whitespace-nowrap">
              {enumLabel("externalList.paymentType", row.type, t)}
            </DataTablePrimaryText>
            <div className="text-surface-500 truncate text-xs">
              {providerFlowLabel(row, t)}
            </div>
          </div>
        ),
      },
      {
        id: "providerRef",
        header: t("externalList.colProviderRef"),
        defaultWidth: 160,
        cell: (row) =>
          row.provider_payment_id ? (
            <ClickToCopyId
              value={row.provider_payment_id}
              className="break-words text-xs text-surface-600"
            />
          ) : (
            t("shared.emptyCell")
          ),
      },
      {
        id: "booking",
        defaultHidden: true,
        header: t("externalList.colBooking"),
        defaultWidth: 120,
        cell: (row) =>
          row.booking_id ? (
            <ClickToCopyId
              value={row.booking_id}
              displayValue={shortId(row.booking_id)}
              className="break-words text-xs text-surface-600"
            />
          ) : (
            t("shared.emptyCell")
          ),
      },
      {
        id: "paymentId",
        defaultHidden: true,
        header: t("externalList.colPaymentId"),
        defaultWidth: 120,
        cell: (row) => (
          <ClickToCopyId
            value={row.id}
            displayValue={shortId(row.id)}
            className="break-words text-xs text-surface-500"
          />
        ),
      },
    ],
    [t, tCommon],
  );

  const linkedPaymentLabel = focusPaymentId ? (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-[13px] text-surface-500">
      {t("externalList.linkedPayment")}
      <ClickToCopyId
        value={focusPaymentId}
        displayValue={shortId(focusPaymentId, 12)}
        className="text-xs"
      />
    </span>
  ) : null;

  const clearHighlightButton = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      leftIcon={<X className="h-4 w-4" />}
      onClick={clearHighlight}
    >
      {t("externalList.clearHighlight")}
    </Button>
  );

  return (
    <PageFrame
      title={t("layout.tabExternal")}
      description={t("layout.externalDescription")}
      icon={CreditCard}
      actions={
        <Button
          type="button"
          variant="outline"
          isLoading={isFetching}
          leftIcon={<RefreshCw className="h-4 w-4" />}
          onClick={() => {
            void refetch();
            void statusCountsQuery.refetch();
          }}
        >
          {tCommon("refresh")}
        </Button>
      }
    >
      <div className="space-y-4">
        <FilterBar>
          <AdminFilterMenu<string | null>
            icon={CircleDot}
            name={t("shared.colStatus")}
            value={statusFilter}
            defaultValue={null}
            onChange={changeStatus}
            options={[
              {
                value: null,
                label: t("externalList.statusAll"),
                count: statusCounts?.total,
              },
              ...(statusCounts?.items ?? []).map(({ status, count }) => ({
                value: status,
                label: enumLabel("externalList.paymentStatus", status, t),
                count,
              })),
            ]}
          />
          <AdminRegionFilter
            value={regionFilter}
            onChange={changeRegion}
            valueKey="id"
          />
        </FilterBar>
        {statusCountsFailure ? (
          <QueryLoadError failure={statusCountsFailure} />
        ) : null}

        {focusedPayment && focusPaymentId ? (
          <div ref={focusCardRef} className="scroll-mt-24">
          <SectionCard
            title={t("externalList.selectedPayment")}
            description={linkedPaymentLabel}
            action={clearHighlightButton}
            className="ring-1 ring-primary-500/25"
          >
            <DefinitionList className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 [&>*]:min-w-0">
              <DetailRow
                label={t("shared.colAmount")}
                valueClassName="font-medium tabular-nums"
              >
                {formatMoney(
                  focusedPayment.amount_cents,
                  focusedPayment.currency,
                )}
              </DetailRow>
              <DetailRow
                label={t("shared.colStatus")}
                valueClassName="mt-0.5"
              >
                <StatusBadge
                  status={focusedPayment.status}
                  label={enumLabel("externalList.paymentStatus", focusedPayment.status, t)}
                />
              </DetailRow>
              <DetailRow label={t("shared.colType")}>
                {enumLabel("externalList.paymentType", focusedPayment.type, t)}
              </DetailRow>
              <DetailRow label={t("externalList.providerFlow")}>
                {providerFlowLabel(focusedPayment, t)}
              </DetailRow>
              <DetailRow
                label={t("externalList.providerPaymentId")}
                className="sm:col-span-2"
                valueClassName="break-all text-xs"
              >
                {focusedPayment.provider_payment_id ? (
                  <ClickToCopyId
                    value={focusedPayment.provider_payment_id}
                    className="text-surface-800"
                  />
                ) : (
                  t("shared.emptyCell")
                )}
              </DetailRow>
              <DetailRow
                label={t("externalList.idempotencyKey")}
                className="sm:col-span-2"
                valueClassName="break-all text-xs"
              >
                <ClickToCopyId
                  value={focusedPayment.idempotency_key}
                  className="text-surface-800"
                />
              </DetailRow>
              <DetailRow
                label={t("externalList.booking")}
                valueClassName="text-xs"
              >
                {focusedPayment.booking_id ? (
                  <ClickToCopyId
                    value={focusedPayment.booking_id}
                    className="text-surface-800"
                  />
                ) : (
                  t("shared.emptyCell")
                )}
              </DetailRow>
              <DetailRow
                label={t("externalList.paymentId")}
                valueClassName="text-xs"
              >
                <ClickToCopyId
                  value={focusedPayment.id}
                  className="text-surface-800"
                />
              </DetailRow>
            </DefinitionList>
          </SectionCard>
          </div>
        ) : focusPaymentId ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-surface-200 bg-surface-0 px-4 py-2.5 shadow-xs">
            {linkedPaymentLabel}
            {clearHighlightButton}
          </div>
        ) : null}

        <DataTable
          labels={tableLabels}
          closeLabel={tCommon("close")}
          loadingLabel={tCommon("loading")}
          rows={items}
          columns={columns}
          getRowKey={(row) => row.id}
          defaultSortColumn="created"
          defaultSortDirection="desc"
          serverPagination={{
            page,
            pageSize: PAGE_SIZE,
            total,
            onPageChange: setPage,
            isFetching,
          }}
          preferencesKey="admin-external-payments"
          onRowClick={(row) => focusPayment(row.id)}
          openRowLabel={(row) =>
            t("externalList.openRowAria", { id: shortId(row.id) })
          }
          loadState={queryLoadState({
            isLoading,
            error,
            errorMessage: t("externalList.loadError"),
            retryLabel: tCommon("tryAgain"),
            onRetry: () => void refetch(),
          })}
          emptyMessage={t("shared.emptyCell")}
          rowClassName={(row) =>
            focusPaymentId === row.id
              ? "bg-primary-50/70 ring-1 ring-inset ring-primary-500/40 dark:bg-primary-50/40"
              : undefined
          }
        />
      </div>
    </PageFrame>
  );
}
